import { useEffect, useState, type RefObject } from "react";
import { teacherPriorityApi } from "@/services/api/teacherPriority";
import type { SubstitutionRequestDocument } from "@/types/teacher-request";

export function TalkRequestDocuments({
  requestId,
  fileInputRef,
  canUpload,
  canDelete,
}: {
  requestId: string;
  fileInputRef: RefObject<HTMLInputElement>;
  canUpload: boolean;
  canDelete: boolean;
}) {
  const [docs, setDocs] = useState<SubstitutionRequestDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    let live = true;
    teacherPriorityApi
      .getRequestDocuments(requestId)
      .then((list) => {
        if (live) setDocs(list);
      })
      .catch(() => {
        if (live) setDocs([]);
      });
    return () => {
      live = false;
    };
  }, [requestId]);

  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      await teacherPriorityApi.uploadRequestDocument(requestId, file);
      setDocs(await teacherPriorityApi.getRequestDocuments(requestId));
    } catch {
      setError("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <section aria-label="Dokumente">
      <p>Dokumente</p>
      {docs.length === 0 ? <p>Noch keine Dokumente</p> : null}
      <ul>
        {docs.map((doc) => (
          <li key={doc.id}>
            <span>{doc.filename}</span>
            {canDelete ? (
              <button
                type="button"
                onClick={() => {
                  void teacherPriorityApi
                    .deleteRequestDocument(requestId, doc.id)
                    .then(() => teacherPriorityApi.getRequestDocuments(requestId))
                    .then(setDocs)
                    .catch(() => setError("Löschen fehlgeschlagen"));
                }}
              >
                Löschen
              </button>
            ) : null}
          </li>
        ))}
      </ul>
      {canUpload ? (
        <label>
          Datei hochladen
          <input
            ref={fileInputRef}
            type="file"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void upload(file);
              event.target.value = "";
            }}
          />
        </label>
      ) : null}
      {uploading ? <p>Wird hochgeladen…</p> : null}
      {error ? <p>{error}</p> : null}
    </section>
  );
}
